import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/modal';
import Input from '../../components/input';
import Select from '../../components/select';
import useI18n from '../../i18n/useI18n';
import { formatPeriodFieldLabel } from './periodLabelUtils';

const defaultFormValues = {
  title: '',
  scopeType: 'university',
  programId: '',
  universityId: '',
  academicYear: '',
  period1StartDate: '',
  period1EndDate: '',
  period1AnnouncementDate: '',
  period2StartDate: '',
  period2EndDate: '',
  period2AnnouncementDate: '',
  period3StartDate: '',
  period3EndDate: '',
  period3AnnouncementDate: '',
  note: '',
  startDate: '',
  endDate: '',
  url: '',
  guideType: 'preference_guide',
  fileUrl: '',
  externalUrl: '',
  description: '',
  isActive: true,
  examType: '',
  examDate: '',
  registrationDeadline: '',
  resultAnnouncementDate: '',
};

const periodIndexes = [1, 2, 3];

const guideTypeOptions = [
  { value: 'preference_guide', labelKey: 'DATE_SCENARIO_GUIDE_TYPE_PREFERENCE_GUIDE' },
];

const DateScenarioModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues = defaultFormValues,
  isEditMode = false,
  universityName = '',
  programs = [],
  universities = [],
  programsLoading = false,
  allowUniversitySelection = true,
  onUniversityChange,
  scenarioType = 'generic',
}) => {
  const { t } = useI18n();
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isExamScenario = scenarioType === 'exam';
  const isRegistrationScenario = scenarioType === 'registrationDates';
  const isPreferenceDatesScenario = scenarioType === 'preferenceDates';
  const isPreferenceGuidesScenario = scenarioType === 'preferenceGuides';
  const shouldShowProgramSelector =
    !isRegistrationScenario && !isPreferenceDatesScenario && !isPreferenceGuidesScenario;
  const examTypeOptions = useMemo(
    () => [
      { value: 'yos', label: t('EXAM_DATES_EXAM_TYPE_YOS') },
      { value: 'sat', label: t('EXAM_DATES_EXAM_TYPE_SAT') },
      { value: 'toefl', label: t('EXAM_DATES_EXAM_TYPE_TOEFL') },
      { value: 'ielts', label: t('EXAM_DATES_EXAM_TYPE_IELTS') },
      { value: 'other', label: t('EXAM_DATES_EXAM_TYPE_OTHER') },
    ],
    [t]
  );

  useEffect(() => {
    if (isOpen) {
      setFormValues({ ...defaultFormValues, ...initialValues });
      setErrors({});
    } else {
      setFormValues(defaultFormValues);
      setErrors({});
      setSubmitting(false);
    }
  }, [isOpen, initialValues]);

  const universityOptions = useMemo(
    () =>
      universities
        .map((university) => {
          const universityId = university.id ?? university.value;
          if (universityId === undefined || universityId === null) return null;
          return {
            value: String(universityId),
            label: university.name ?? university.title ?? '-',
          };
        })
        .filter(Boolean),
    [universities]
  );

  const programOptions = useMemo(
    () =>
      programs
        .map((program) => {
          const programId = program.id ?? program.programId ?? program.value;
          if (programId === undefined || programId === null) return null;
          return {
            value: String(programId),
            label: program.name ?? program.label ?? '-',
          };
        })
        .filter(Boolean),
    [programs]
  );

  const setField = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleUniversitySelect = (value) => {
    setField('universityId', value);
    setField('programId', '');
    if (typeof onUniversityChange === 'function') {
      onUniversityChange(value);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!formValues.universityId) {
      nextErrors.universityId = t('DATE_SCENARIO_MODAL_VALIDATION_UNIVERSITY');
    }

    if (isPreferenceGuidesScenario) {
      if (!formValues.academicYear || Number.isNaN(parseInt(formValues.academicYear, 10))) {
        nextErrors.academicYear = t('DATE_SCENARIO_MODAL_VALIDATION_ACADEMIC_YEAR');
      }
      if (!formValues.title || !formValues.title.trim()) {
        nextErrors.title = t('DATE_SCENARIO_MODAL_VALIDATION_TITLE');
      }
      if (!formValues.fileUrl || !formValues.fileUrl.trim()) {
        nextErrors.fileUrl = t('DATE_SCENARIO_MODAL_VALIDATION_FILE_URL');
      }
    } else if (isPreferenceDatesScenario || isRegistrationScenario) {
      if (!formValues.academicYear || Number.isNaN(parseInt(formValues.academicYear, 10))) {
        nextErrors.academicYear = t('DATE_SCENARIO_MODAL_VALIDATION_ACADEMIC_YEAR');
      }
      if (!formValues.period1StartDate) {
        nextErrors.period1StartDate = t('DATE_SCENARIO_MODAL_VALIDATION_PERIOD_REQUIRED');
      }
      if (!formValues.period1EndDate) {
        nextErrors.period1EndDate = t('DATE_SCENARIO_MODAL_VALIDATION_PERIOD_REQUIRED');
      }
      if (isPreferenceDatesScenario && !formValues.period1AnnouncementDate) {
        nextErrors.period1AnnouncementDate = t('DATE_SCENARIO_MODAL_VALIDATION_PERIOD_REQUIRED');
      }
    } else if (isExamScenario) {
      if (!formValues.academicYear || Number.isNaN(parseInt(formValues.academicYear, 10))) {
        nextErrors.academicYear = t('DATE_SCENARIO_MODAL_VALIDATION_ACADEMIC_YEAR');
      }
      if (!formValues.examType) {
        nextErrors.examType = t('EXAM_DATES_MODAL_VALIDATION_EXAM_TYPE');
      }
      if (!formValues.examDate) {
        nextErrors.examDate = t('EXAM_DATES_MODAL_VALIDATION_EXAM_DATE');
      }
      if (!formValues.registrationDeadline) {
        nextErrors.registrationDeadline = t('EXAM_DATES_MODAL_VALIDATION_REGISTRATION_DEADLINE');
      }
    } else {
      if (!formValues.title || !formValues.title.trim()) {
        nextErrors.title = t('DATE_SCENARIO_MODAL_VALIDATION_TITLE');
      }
      if (!formValues.startDate) {
        nextErrors.startDate = t('DATE_SCENARIO_MODAL_VALIDATION_START');
      }
      if (!formValues.endDate) {
        nextErrors.endDate = t('DATE_SCENARIO_MODAL_VALIDATION_END');
      }
      if (
        formValues.startDate &&
        formValues.endDate &&
        new Date(formValues.endDate).getTime() < new Date(formValues.startDate).getTime()
      ) {
        nextErrors.endDate = t('DATE_SCENARIO_MODAL_VALIDATION_END_AFTER_START');
      }
      if (formValues.scopeType === 'program' && !formValues.programId) {
        nextErrors.programId = t('DATE_SCENARIO_MODAL_VALIDATION_PROGRAM');
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      await onSubmit({ ...formValues, programId: formValues.programId || '' });
    } catch {
      // handled upstream
    } finally {
      setSubmitting(false);
    }
  };

  const scopeOptions = [
    { value: 'university', label: t('DATE_SCENARIO_SCOPE_UNIVERSITY') },
    { value: 'program', label: t('DATE_SCENARIO_SCOPE_PROGRAM') },
  ];

  const renderPeriodRows = (includeAnnouncement) =>
    periodIndexes.map((index) => (
      <div className="row" key={index}>
        <div className="col-md-6">
          <div className="form-group">
            <label>
              {formatPeriodFieldLabel(t, 'DATE_SCENARIO_MODAL_FIELD_PERIOD_START', index)}
              {index === 1 && <span className="text-danger m-l-5">*</span>}
            </label>
            <Input
              type="date"
              name={`period${index}StartDate`}
              value={formValues[`period${index}StartDate`]}
              onChange={(e) => setField(`period${index}StartDate`, e.target.value)}
              required={index === 1}
            />
            {errors[`period${index}StartDate`] && (
              <small className="text-danger">{errors[`period${index}StartDate`]}</small>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>
              {formatPeriodFieldLabel(t, 'DATE_SCENARIO_MODAL_FIELD_PERIOD_END', index)}
              {index === 1 && <span className="text-danger m-l-5">*</span>}
            </label>
            <Input
              type="date"
              name={`period${index}EndDate`}
              value={formValues[`period${index}EndDate`]}
              onChange={(e) => setField(`period${index}EndDate`, e.target.value)}
              required={index === 1}
            />
            {errors[`period${index}EndDate`] && (
              <small className="text-danger">{errors[`period${index}EndDate`]}</small>
            )}
          </div>
        </div>
        {includeAnnouncement && (
          <div className="col-md-12">
            <div className="form-group">
              <label>
                {formatPeriodFieldLabel(t, 'DATE_SCENARIO_MODAL_FIELD_PERIOD_ANNOUNCEMENT', index)}
                {index === 1 && <span className="text-danger m-l-5">*</span>}
              </label>
              <Input
                type="date"
                name={`period${index}AnnouncementDate`}
                value={formValues[`period${index}AnnouncementDate`]}
                onChange={(e) =>
                  setField(`period${index}AnnouncementDate`, e.target.value)
                }
                required={index === 1}
              />
              {errors[`period${index}AnnouncementDate`] && (
                <small className="text-danger">
                  {errors[`period${index}AnnouncementDate`]}
                </small>
              )}
            </div>
          </div>
        )}
      </div>
    ));

  const renderPeriodOnlyFields = () => (
    <>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_ACADEMIC_YEAR')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="number"
              name="academicYear"
              value={formValues.academicYear}
              onChange={(e) => setField('academicYear', e.target.value)}
              placeholder="2026"
            />
            {errors.academicYear && <small className="text-danger">{errors.academicYear}</small>}
          </div>
        </div>
      </div>
      {renderPeriodRows(false)}
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</label>
            <textarea
              className="form-control"
              rows={4}
              name="note"
              value={formValues.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderPreferenceDateFields = () => (
    <>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_ACADEMIC_YEAR')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="number"
              name="academicYear"
              value={formValues.academicYear}
              onChange={(e) => setField('academicYear', e.target.value)}
              placeholder="2026"
            />
            {errors.academicYear && <small className="text-danger">{errors.academicYear}</small>}
          </div>
        </div>
      </div>
      {renderPeriodRows(true)}
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</label>
            <textarea
              className="form-control"
              rows={4}
              name="note"
              value={formValues.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderPreferenceGuideFields = () => (
    <>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_ACADEMIC_YEAR')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="number"
              name="academicYear"
              value={formValues.academicYear}
              onChange={(e) => setField('academicYear', e.target.value)}
              placeholder="2026"
            />
            {errors.academicYear && <small className="text-danger">{errors.academicYear}</small>}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_GUIDE_TYPE')}</label>
            <Select
              name="guideType"
              value={formValues.guideType}
              onChange={(e) => setField('guideType', e.target.value)}
              options={guideTypeOptions.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              icon="zmdi-file"
            />
          </div>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <div className="form-group">
            <label className="d-block">{t('DATE_SCENARIO_MODAL_FIELD_STATUS')}</label>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="guideStatusToggle"
                checked={!!formValues.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="guideStatusToggle">
                {formValues.isActive
                  ? t('DATE_SCENARIO_STATUS_ACTIVE')
                  : t('DATE_SCENARIO_STATUS_INACTIVE')}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_TITLE')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              name="title"
              value={formValues.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_TITLE_PLACEHOLDER')}
              required
            />
            {errors.title && <small className="text-danger">{errors.title}</small>}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_FILE_URL')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="url"
              name="fileUrl"
              value={formValues.fileUrl}
              onChange={(e) => setField('fileUrl', e.target.value)}
              placeholder="https://example.com/guide.pdf"
              required
            />
            {errors.fileUrl && <small className="text-danger">{errors.fileUrl}</small>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_EXTERNAL_URL')}</label>
            <Input
              type="url"
              name="externalUrl"
              value={formValues.externalUrl}
              onChange={(e) => setField('externalUrl', e.target.value)}
              placeholder="https://university.edu/guide"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_DESCRIPTION')}</label>
            <textarea
              className="form-control"
              rows={4}
              name="description"
              value={formValues.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderExamFields = () => (
    <>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_ACADEMIC_YEAR')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="number"
              name="academicYear"
              value={formValues.academicYear}
              onChange={(e) => setField('academicYear', e.target.value)}
              placeholder="2026"
            />
            {errors.academicYear && <small className="text-danger">{errors.academicYear}</small>}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('EXAM_DATES_MODAL_FIELD_EXAM_TYPE')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Select
              name="examType"
              value={formValues.examType}
              onChange={(e) => setField('examType', e.target.value)}
              options={examTypeOptions}
              icon="zmdi-label"
            />
            {errors.examType && <small className="text-danger">{errors.examType}</small>}
          </div>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <div className="form-group w-100">
            <label className="d-block">{t('DATE_SCENARIO_MODAL_FIELD_STATUS')}</label>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="examStatusToggle"
                checked={!!formValues.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="examStatusToggle">
                {formValues.isActive
                  ? t('DATE_SCENARIO_STATUS_ACTIVE')
                  : t('DATE_SCENARIO_STATUS_INACTIVE')}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('EXAM_DATES_MODAL_FIELD_EXAM_DATE')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="date"
              name="examDate"
              value={formValues.examDate}
              onChange={(e) => setField('examDate', e.target.value)}
            />
            {errors.examDate && <small className="text-danger">{errors.examDate}</small>}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>
              {t('EXAM_DATES_MODAL_FIELD_REGISTRATION_DEADLINE')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="date"
              name="registrationDeadline"
              value={formValues.registrationDeadline}
              onChange={(e) => setField('registrationDeadline', e.target.value)}
            />
            {errors.registrationDeadline && (
              <small className="text-danger">{errors.registrationDeadline}</small>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="form-group">
            <label>{t('EXAM_DATES_MODAL_FIELD_RESULT_ANNOUNCEMENT')}</label>
            <Input
              type="date"
              name="resultAnnouncementDate"
              value={formValues.resultAnnouncementDate}
              onChange={(e) => setField('resultAnnouncementDate', e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</label>
            <textarea
              className="form-control"
              rows={4}
              name="note"
              value={formValues.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderGenericFields = () => (
    <>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_TITLE')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              name="title"
              value={formValues.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_TITLE_PLACEHOLDER')}
              required
            />
            {errors.title && <small className="text-danger">{errors.title}</small>}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_SCOPE')}</label>
            <Select
              name="scopeType"
              value={formValues.scopeType}
              onChange={(e) => setField('scopeType', e.target.value)}
              options={scopeOptions}
              icon="zmdi-layers"
            />
          </div>
        </div>
        {formValues.scopeType === 'program' && (
          <div className="col-md-8">
            <div className="form-group">
              <label>
                {t('DATE_SCENARIO_MODAL_FIELD_PROGRAM')}
                <span className="text-danger m-l-5">*</span>
              </label>
              <Select
                name="programId"
                value={formValues.programId}
                onChange={(e) => setField('programId', e.target.value)}
                options={programOptions}
                placeholder={t('DATE_SCENARIO_MODAL_FIELD_PROGRAM_PLACEHOLDER')}
                icon="zmdi-graduation-cap"
                loading={programsLoading}
              />
              {errors.programId && <small className="text-danger">{errors.programId}</small>}
            </div>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_START')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="datetime-local"
              name="startDate"
              value={formValues.startDate}
              onChange={(e) => setField('startDate', e.target.value)}
              required
            />
            {errors.startDate && <small className="text-danger">{errors.startDate}</small>}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label>
              {t('DATE_SCENARIO_MODAL_FIELD_END')}
              <span className="text-danger m-l-5">*</span>
            </label>
            <Input
              type="datetime-local"
              name="endDate"
              value={formValues.endDate}
              onChange={(e) => setField('endDate', e.target.value)}
              required
            />
            {errors.endDate && <small className="text-danger">{errors.endDate}</small>}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_URL')}</label>
            <Input
              type="url"
              name="url"
              value={formValues.url}
              onChange={(e) => setField('url', e.target.value)}
              placeholder="https://example.com"
            />
          </div>
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <div className="form-group">
            <label className="d-block">{t('DATE_SCENARIO_MODAL_FIELD_STATUS')}</label>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="genericStatusToggle"
                checked={!!formValues.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="genericStatusToggle">
                {formValues.isActive
                  ? t('DATE_SCENARIO_STATUS_ACTIVE')
                  : t('DATE_SCENARIO_STATUS_INACTIVE')}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <label>{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</label>
            <textarea
              className="form-control"
              rows={4}
              name="note"
              value={formValues.note}
              onChange={(e) => setField('note', e.target.value)}
              placeholder={t('DATE_SCENARIO_MODAL_FIELD_NOTE_PLACEHOLDER')}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderBody = () => {
    if (isPreferenceGuidesScenario) return renderPreferenceGuideFields();
    if (isPreferenceDatesScenario) return renderPreferenceDateFields();
    if (isRegistrationScenario) return renderPeriodOnlyFields();
    if (isExamScenario) return renderExamFields();
    return renderGenericFields();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? undefined : onClose}
      title={isEditMode ? t('DATE_SCENARIO_MODAL_TITLE_EDIT') : t('DATE_SCENARIO_MODAL_TITLE_NEW')}
      subtitle={universityName}
      icon="zmdi-calendar"
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className={`col-md-${shouldShowProgramSelector ? '6' : '12'}`}>
            <div className="form-group">
              <label>
                {t('DATE_SCENARIO_MODAL_FIELD_UNIVERSITY')}
                <span className="text-danger m-l-5">*</span>
              </label>
              <Select
                name="universityId"
                value={formValues.universityId}
                onChange={(e) => handleUniversitySelect(e.target.value)}
                options={universityOptions}
                disabled={!allowUniversitySelection}
                placeholder={t('DATE_SCENARIO_MODAL_FIELD_UNIVERSITY_PLACEHOLDER')}
                icon="zmdi-city"
              />
              {errors.universityId && <small className="text-danger">{errors.universityId}</small>}
            </div>
          </div>
          {shouldShowProgramSelector && (
            <div className="col-md-6">
              <div className="form-group">
                <label>{t('DATE_SCENARIO_MODAL_FIELD_PROGRAM')}</label>
                <Select
                  name="programId"
                  value={formValues.programId}
                  onChange={(e) => setField('programId', e.target.value)}
                  options={programOptions}
                  placeholder={t('DATE_SCENARIO_MODAL_FIELD_PROGRAM_PLACEHOLDER')}
                  icon="zmdi-graduation-cap"
                  disabled={!formValues.universityId}
                  loading={programsLoading}
                />
                {errors.programId && <small className="text-danger">{errors.programId}</small>}
              </div>
            </div>
          )}
        </div>

        {renderBody()}

        <div className="row m-t-10">
          <div className="col-md-12 text-right">
            <button
              type="button"
              className="btn btn-default btn-simple"
              onClick={onClose}
              disabled={submitting}
            >
              {t('DATE_SCENARIO_MODAL_CANCEL')}
            </button>
            <button type="submit" className="btn btn-primary m-l-10" disabled={submitting}>
              {isEditMode
                ? t('DATE_SCENARIO_MODAL_SUBMIT_UPDATE')
                : t('DATE_SCENARIO_MODAL_SUBMIT_CREATE')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default DateScenarioModal;

